// Contenido editorial de DocEnLinea: FAQs y textos legales (disclaimers).
//
// AVISO LEGAL: el texto de los disclaimers es una PLANTILLA. Debe ser revisado
// por un abogado colombiano antes de considerarse definitivo. No constituye
// asesoría legal formal.

import { describePricing, describeDoctorEarnings } from './pricing';

// ── Guías de salud ───────────────────────────────────────────────────────────
// Páginas estáticas de public/ (consulta-online-*.html). Se enlazan con <a>, no
// con <Link>: no son rutas de React y el router las mandaría a la home.
export const HEALTH_GUIDES = [
  { href: '/consulta-online-infeccion-urinaria.html', label: 'Infección urinaria' },
  { href: '/consulta-online-dolor-de-garganta.html', label: 'Dolor de garganta' },
  { href: '/consulta-online-gripa-resfriado.html', label: 'Gripa o resfriado' },
];

// ── FAQ (orientado al paciente) ──────────────────────────────────────────────
// Recibe la tarifa del backend (usePricing). Con pricing null el texto de
// precio queda neutro: nunca se muestra un valor inventado.
export const buildFaqs = (pricing) => [
  {
    q: '¿Qué es DocEnLinea?',
    a: 'DocEnLinea es una plataforma de telemedicina que te conecta con médicos generales colombianos a través de videollamada y chat, para que recibas atención médica sin salir de casa.',
  },
  {
    q: '¿Cómo agendo una cita?',
    a: '1. Regístrate como paciente.\n2. Elige el médico y el horario que mejor te quede.\n3. Realiza el pago seguro de la consulta.\n4. Recibirás la confirmación por correo y un recordatorio por WhatsApp 5 minutos antes de tu cita.',
  },
  {
    q: '¿Cuánto cuesta una consulta?',
    a: `${describePricing(pricing)} El pago se realiza de forma segura al momento de agendar y verás el valor exacto de tu cita antes de confirmarla.`,
  },
  {
    q: '¿Cómo ingreso a mi consulta?',
    a: 'Puedes entrar desde tu panel de paciente o directamente desde el link que te llega por WhatsApp. Solo necesitas permitir el acceso a tu cámara y micrófono.',
  },
  {
    q: '¿Qué necesito para mi consulta?',
    a: '- Un dispositivo con cámara y micrófono (celular o computador).\n- Conexión a internet estable.\n- Un espacio tranquilo y privado.',
  },
  {
    q: '¿Mi información es confidencial?',
    a: 'Sí. Tu información y el contenido de la consulta se tratan de forma confidencial conforme a la Ley 1581 de 2012 (Protección de Datos Personales).',
  },
  {
    q: '¿El médico puede enviar fórmula o recetas?',
    a: 'Sí. El médico puede generar fórmulas médicas y recomendaciones dentro de la plataforma.',
  },
  {
    q: '¿DocEnLinea atiende emergencias?',
    a: 'NO. DocEnLinea NO es un servicio de atención de emergencias. Si tienes una urgencia que pone en riesgo tu vida, llama al 123 o acude al servicio de urgencias más cercano.',
  },
  {
    q: '¿Puedo cancelar o reprogramar mi cita?',
    a: 'Puedes cancelar o reprogramar según nuestra política. Comunícate con soporte para gestionar cambios.',
  },
  {
    q: '¿Cómo recibo soporte?',
    a: 'A través del canal de soporte disponible en la plataforma o escribiendo a nuestro correo de contacto.',
  },
];

// ── Disclaimer del PACIENTE (registro: consentimiento + términos) ─────────────
export const buildPatientDisclaimer = (pricing) => ({
  title: 'Términos y Condiciones para Pacientes — Consentimiento Informado',
  intro:
    'Al registrarte y utilizar DocEnLinea, declaras que has leído, comprendido y aceptas lo siguiente:',
  clauses: [
    {
      heading: 'Naturaleza del servicio.',
      text: 'DocEnLinea es una plataforma tecnológica que pone en contacto a pacientes con profesionales de la medicina independiente. DocEnLinea NO presta servicios médicos y NO emplea a los médicos que aparecen en la plataforma. El médico es el único responsable del acto médico, el diagnóstico y el tratamiento.',
    },
    {
      heading: 'Limitaciones de la telemedicina.',
      text: 'La consulta virtual puede no ser suficiente para todos los casos de salud. Algunas condiciones requieren examen físico presencial o pruebas diagnósticas que no son posibles en una videollamada. El médico indicará si tu caso necesita atención presencial.',
    },
    {
      heading: 'No es servicio de emergencias.',
      text: 'DocEnLinea NO atiende emergencias ni urgencias vitales. Ante una urgencia, contacta al 123 o dirígete al servicio de urgencias más cercano.',
    },
    {
      heading: 'Responsabilidad médica.',
      text: 'DocEnLinea actúa únicamente como intermediario tecnológico y NO asume responsabilidad por el actuar del médico, errores diagnósticos, omisiones o el resultado del tratamiento. Cualquier reclamación sobre la atención médica deberá dirigirse directamente al profesional tratante.',
    },
    {
      heading: 'Veracidad de la información.',
      text: 'Te comprometes a proporcionar información veraz sobre tu estado de salud, antecedentes y medicamentos. Eres responsable de las consecuencias derivadas de información falsa o incompleta.',
    },
    {
      heading: 'Autorización de tratamiento de datos.',
      text: 'Autorizas a DocEnLinea para recolectar, almacenar y tratar tus datos personales conforme a la Ley 1581 de 2012 y el Decreto 1377 de 2013, con la finalidad de gestionar tu cuenta, agendamiento y atención médica.',
    },
    {
      heading: 'Pago.',
      text: `El valor de la consulta se cancela al momento de agendar y se muestra antes de confirmar la cita. ${describePricing(pricing)} La transacción se procesa a través del proveedor de pagos.`,
    },
    {
      heading: 'Menores.',
      text: 'Si registras a un menor, confirmas que eres su representante legal y autorizas su atención.',
    },
    {
      heading: 'Chat de la consulta.',
      text: 'El chat de la consulta es privado entre el médico y el paciente. Sus mensajes se conservan únicamente durante el resto del día y se eliminan automáticamente todos los días a las 11:59 p. m. (hora de Colombia).',
    },
  ],
  closing:
    'Al marcar la casilla de aceptación, manifiestas tu conformidad con estos términos.',
});

// ── Disclaimer del MÉDICO (registro: términos prestador + datos + Nequi) ──────
export const buildDoctorDisclaimer = (pricing) => ({
  title: 'Términos y Condiciones para Profesionales de la Salud',
  intro:
    'Al registrarte como profesional en DocEnLinea, declaras que has leído, comprendido y aceptas:',
  clauses: [
    {
      heading: 'Independencia profesional.',
      text: 'Eres un profesional independiente. DocEnLinea es un intermediario tecnológico y NO es tu empleador ni genera vinculación laboral. Tú asumes la responsabilidad total del acto médico, el diagnóstico y el tratamiento de los pacientes.',
    },
    {
      heading: 'Habilitación.',
      text: 'Declaras que cuentas con título profesional, registro médico y matrícula profesional vigente, y que estás habilitado legalmente para ejercer la medicina en Colombia. DocEnLinea podrá verificar estos documentos.',
    },
    {
      heading: 'Marco legal de la telemedicina.',
      text: 'Te comprometes a ejercer conforme a la normatividad colombiana aplicable a la telemedicina (Ley 1419 de 2010, Decreto 1011 de 2007, Resolución 2654 de 2019 y normas concordantes).',
    },
    {
      heading: 'Historia clínica.',
      text: 'Eres responsable del registro de la historia clínica de cada atención conforme a la Ley 23 de 1981 y la Resolución 1995 de 1999.',
    },
    {
      heading: 'Tratamiento de datos.',
      text: 'Autorizas el tratamiento de tus datos personales conforme a la Ley 1581 de 2012, con fines de gestión de tu cuenta, agenda y pagos.',
    },
    {
      heading: 'Pagos y comisión.',
      text: `El paciente paga el valor de la consulta a través de la plataforma. ${describePricing(pricing)} ${describeDoctorEarnings(pricing)}`,
    },
    {
      heading: 'Cuenta Nequi.',
      text: 'Debes registrar un número de Nequi válido a tu nombre para recibir los pagos. Eres responsable de la veracidad de los datos de la cuenta. DocEnLinea no se hace responsable por transferencias a cuentas mal registradas por ti.',
    },
    {
      heading: 'Obligaciones de agenda.',
      text: 'Te comprometes a atender las citas en los horarios agendados y a mantener un trato respetuoso y profesional con los pacientes.',
    },
    {
      heading: 'Chat de la consulta.',
      text: 'El chat de la consulta es privado entre el médico y el paciente. Sus mensajes se conservan únicamente durante el resto del día y se eliminan automáticamente todos los días a las 11:59 p. m. (hora de Colombia).',
    },
  ],
  closing:
    'Al marcar la casilla de aceptación, manifiestas tu conformidad con estos términos.',
});

// Nota de plantilla legal que se muestra bajo cada disclaimer.
export const LEGAL_TEMPLATE_NOTE =
  '';
